import React, { useState } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { FaPaperPlane } from "react-icons/fa";
import "bootstrap/dist/css/bootstrap.min.css";
import "../shared/chat.css";

function getModelFromBikeName(name) {
  if (!name) return null;

  return name
    .replace(/^Matchless\s+/i, "")
    .replace(/^AJS\s+/i, "")
    .replace(/^WD\s+/i, "")
    .trim();
}

async function fetchSignedUrlForS3Key(s3Key) {
  if (!s3Key) return "";

  const res = await fetch("/.netlify/functions/manual-visual-signed-urls", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      keys: [s3Key],
    }),
  });

  const data = await res.json().catch(() => null);

  if (!res.ok) {
    throw new Error(data?.error || "Failed to generate signed image URL");
  }

  const match = Array.isArray(data?.results)
    ? data.results.find((x) => x.key === s3Key)
    : null;

  return match?.url || "";
}

const BikeManualChatModal = ({ show, onHide, bike }) => {
  const [input, setInput] = useState("");
  const [botResponse, setBotResponse] = useState("");
  const [botImages, setBotImages] = useState([]);
  const [loading, setLoading] = useState(false);

  if (!bike) return null;

  const fetchMessage = async () => {
    const response = await fetch(
      "https://im6nc6crmemhhpkldwk53u4rpi0aggam.lambda-url.eu-north-1.on.aws/",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          question: input,
          bike: {
            name: bike.name,
            model: getModelFromBikeName(bike.name),
            year: bike.year,
            engine: bike.engine,
            gearbox: bike.gearbox,
            topSpeed: bike.topSpeed,
            horsepower: bike.horsepower,
            description: bike.description,
            commonIssues: bike.commonIssues,
            maintenance: bike.maintenance,
          },
        }),
      }
    );

    const data = await response.json().catch(() => null);
console.log(data)
    if (!response.ok) {
      console.error(data);
      return {
        answer: "Sorry, I had trouble answering that.",
        images: [],
      };
    }

    console.log("Jam-Bot debug:", data?.debug);

    const rawImages = Array.isArray(data?.images) ? data.images : [];

const hydratedImages = await Promise.all(
  rawImages.map(async (img, index) => {
    const id = img?.id ?? img?.Id ?? `img-${index}`;
    const title = img?.title ?? img?.Title ?? "Manual diagram";
    const caption = img?.caption ?? img?.Caption ?? "";
    const pageNumber = img?.pageNumber ?? img?.PageNumber ?? null;
    const visualType = img?.visualType ?? img?.VisualType ?? "diagram";
    const imageS3Key = img?.imageS3Key ?? img?.ImageS3Key ?? "";

    try {
      const imageUrl = imageS3Key
        ? await fetchSignedUrlForS3Key(imageS3Key)
        : "";

      return {
        id,
        title,
        caption,
        pageNumber,
        visualType,
        imageUrl,
      };
    } catch (err) {
      console.error("Failed to hydrate chat image:", err, img);
      return {
        id,
        title,
        caption,
        pageNumber,
        visualType,
        imageUrl: "",
      };
    }
  })
);
    return {
      answer: data?.answer?.trim() || "No response received.",
      images: hydratedImages.filter((x) => x.imageUrl),
    };
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    setLoading(true);
    setBotResponse("");
    setBotImages([]);

    try {
      const result = await fetchMessage();
      setBotResponse(result.answer);
      setBotImages(result.images);
    } catch (err) {
      console.error(err);
      setBotResponse("Sorry, I had trouble answering that.");
      setBotImages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setInput("");
    setBotResponse("");
    setBotImages([]);
    setLoading(false);
    onHide();
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      dialogClassName="jam-bot-modal jam-bot-modal-manual"
      contentClassName="jam-bot-modal-content"
    >
      <Modal.Header closeButton className="jam-bot-modal-header">
        <div>
          <div className="jam-bot-kicker">Instruction Book Assistant</div>
          <Modal.Title className="jam-bot-modal-title">
            {bike.name}
          </Modal.Title>
        </div>
      </Modal.Header>

      <Modal.Body className="jam-bot-modal-body">
        <p className="jam-bot-description" style={{ marginBottom: "1rem" }}>
          Ask about maintenance, adjustments, servicing, and workshop procedures
          for this specific bike.
        </p>

        <div className="jam-bot-selected-bike" style={{ marginBottom: "1rem" }}>
          Working on: <strong>{bike.name}</strong> <span>({bike.year})</span>
        </div>

        <label className="jam-bot-label">Question</label>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              sendMessage();
            }
          }}
          placeholder={`Ask something about the ${bike.name}...`}
          className="jam-bot-textarea"
          rows={4}
        />

        <Button
          onClick={sendMessage}
          disabled={loading || !input.trim()}
          className="jam-bot-send"
          style={{ marginTop: "1rem" }}
        >
          {loading ? "Thinking..." : <>Consult Manual <FaPaperPlane style={{ marginLeft: 8 }} /></>}
        </Button>

        {botResponse && (
          <div className="jam-bot-answer" style={{ marginTop: "1.5rem" }}>
            {botResponse.split("\n").map((line, index) => (
              <p key={index}>{line}</p>
            ))}
          </div>
        )}

        {botImages.length > 0 && (
          <div style={{ marginTop: "1.5rem" }}>
            <div
              style={{
                fontWeight: 700,
                marginBottom: "0.9rem",
                fontSize: "1rem",
              }}
            >
              Relevant diagrams
            </div>

            <div
              style={{
                display: "grid",
                gap: "1rem",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              }}
            >
              {botImages.map((img) => (
                <div
                  key={img.id}
                  style={{
                    border: "1px solid rgba(148,163,184,0.25)",
                    borderRadius: 14,
                    overflow: "hidden",
                    background: "rgba(255,255,255,0.03)",
                  }}
                >
                  <img
                    src={img.imageUrl}
                    alt={img.title || "Manual diagram"}
                    style={{
                      width: "100%",
                      height: 180,
                      objectFit: "contain",
                      display: "block",
                      background: "#fff",
                    }}
                  />

                  <div style={{ padding: "0.85rem" }}>
                    <div style={{ fontWeight: 700, marginBottom: 6 }}>
                      {img.title || "Manual diagram"}
                    </div>

                    <div
                      style={{
                        opacity: 0.8,
                        fontSize: ".9rem",
                        marginBottom: img.caption ? 6 : 0,
                      }}
                    >
                      {img.pageNumber ? `Page ${img.pageNumber}` : img.visualType}
                    </div>

                    {img.caption && (
                      <div
                        style={{
                          fontSize: ".92rem",
                          lineHeight: 1.45,
                          opacity: 0.92,
                        }}
                      >
                        {img.caption}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="jam-bot-modal-footer">
        <Button
          variant="secondary"
          onClick={handleClose}
          className="jam-bot-secondary-btn"
        >
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BikeManualChatModal;
